import { useCallback, useRef, useState } from 'react';
import type { DataGlueGraph } from '@glueapp/graph-types';
import { useAgentWebSocket, type AgentWebSocketMessage } from '../api/useAgentWebSocket';
import type { ConnectionState } from '../api/useAgentWebSocket';

// ─── Types ─────────────────────────────────────────────────────────────

export type AgentStatus = 'idle' | 'thinking' | 'tool_calling' | 'error';
export type SessionStatus = 'idle' | 'starting' | 'active' | 'paused' | 'ended';

export interface ActivityEntry {
    id: string;
    type: 'user_message' | 'thinking' | 'tool_call' | 'tool_result' | 'error' | 'run_request' | 'run_status' | 'debug_snapshot';
    content: string;
    timestamp: Date;
    toolName?: string;
    /** For run_request entries: the permission request ID */
    requestId?: string;
    /** For run_request entries: current state of the permission request */
    runRequestStatus?: 'pending' | 'approved' | 'denied' | 'timed_out';
    /** For debug_snapshot entries: workspace file contents */
    debugFiles?: Record<string, string>;
}

export interface TokenStepDetail {
    stepNumber: number;
    timestamp: string;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    noCacheInputTokens: number;
    reasoningTokens: number;
    toolNames: string[];
    finishReason: string;
    cumulativeInputTokens: number;
    cumulativeOutputTokens: number;
}

export interface TokenUsage {
    totalInput: number;
    totalOutput: number;
    steps: TokenStepDetail[];
}

export interface SpatialContextPayload {
    agentPosition: { x: number; y: number };
    functionId: string | null;
    nearbyNodes: Array<{
        nodeId: number;
        nodeType: string;
        name: string;
        position: { x: number; y: number };
        distance: number;
    }>;
}

interface UseAgentSessionOptions {
    graphId: string;
    graph: DataGlueGraph | undefined;
    metadata: { name?: string; description?: string } | undefined;
    /** Optional spatial context to include with session:start messages */
    spatialContext?: SpatialContextPayload;
}

export interface UseAgentSessionReturn {
    // Panel visibility
    isOpen: boolean;
    toggle: () => void;
    close: () => void;

    // Connection
    connectionState: ConnectionState;

    // Session
    sessionId: string | null;
    sessionStatus: SessionStatus;
    agentStatus: AgentStatus;

    // Model
    model: 'sonnet' | 'opus' | 'haiku';
    setModel: (model: 'sonnet' | 'opus' | 'haiku') => void;

    // Activity stream
    entries: ActivityEntry[];

    // Token usage
    tokenUsage: TokenUsage;

    // Run permission
    autoApproveRuns: boolean;
    setAutoApproveRuns: (enabled: boolean) => void;
    respondToRunRequest: (requestId: string, granted: boolean) => void;

    // Actions
    submit: (text: string) => void;
    cancelOperation: () => void;

    // Debug mode
    debugMode: boolean;
    setDebugMode: (enabled: boolean) => void;

    // Spotlight: the node ID the agent is currently working on
    activeNodeId: number | null;
}

// ─── Entry ID Generation ───────────────────────────────────────────────

let entryCounter = 0;
function nextEntryId(): string {
    return `entry-${++entryCounter}-${Date.now()}`;
}

// ─── Hook ──────────────────────────────────────────────────────────────

export function useAgentSession({ graphId, graph, metadata, spatialContext }: UseAgentSessionOptions): UseAgentSessionReturn {
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [sessionStatus, setSessionStatus] = useState<SessionStatus>('idle');
    const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
    const [model, setModel] = useState<'sonnet' | 'opus' | 'haiku'>('sonnet');
    const [entries, setEntries] = useState<ActivityEntry[]>([]);
    const [tokenUsage, setTokenUsage] = useState<TokenUsage>({ totalInput: 0, totalOutput: 0, steps: [] });
    const [autoApproveRuns, setAutoApproveRunsState] = useState(false);
    const [debugMode, setDebugModeState] = useState(true);
    const [activeNodeId, setActiveNodeId] = useState<number | null>(null);

    // Refs for values needed inside the message handler without causing re-renders
    const sessionIdRef = useRef<string | null>(null);
    const graphRef = useRef(graph);
    graphRef.current = graph;
    const spatialContextRef = useRef(spatialContext);
    spatialContextRef.current = spatialContext;

    // ─── Message Handler ─────────────────────────────────────────────

    const handleMessage = useCallback((message: AgentWebSocketMessage) => {
        const { type, payload } = message;

        switch (type) {
            case 'connected': {
                // If we had an active session, auto-resume on reconnect
                const previousSessionId = sessionIdRef.current;
                if (previousSessionId) {
                    // Send resume after a short delay so the handler below can use `send`
                    // This is handled by the reconnect logic — see the effect below
                }
                break;
            }

            case 'session:started': {
                const startedSessionId = payload.sessionId as string;
                setSessionId(startedSessionId);
                sessionIdRef.current = startedSessionId;
                setSessionStatus('active');
                setAgentStatus('thinking');
                break;
            }

            case 'session:paused': {
                setSessionStatus('paused');
                setAgentStatus('idle');
                setActiveNodeId(null);
                break;
            }

            case 'session:ended': {
                setSessionStatus('ended');
                setAgentStatus('idle');
                setActiveNodeId(null);
                setSessionId(null);
                sessionIdRef.current = null;
                break;
            }

            case 'agent:thinking': {
                setActiveNodeId(null);
                const text = payload.text as string;
                setEntries(prev => {
                    const last = prev[prev.length - 1];
                    if (last?.type === 'thinking') {
                        // Append to current thinking block
                        const updated = [...prev];
                        updated[updated.length - 1] = {
                            ...last,
                            content: last.content + text,
                        };
                        return updated;
                    }
                    // Start new thinking block
                    return [
                        ...prev,
                        {
                            id: nextEntryId(),
                            type: 'thinking' as const,
                            content: text,
                            timestamp: new Date(),
                        },
                    ];
                });
                break;
            }

            case 'agent:status': {
                const status = payload.status as AgentStatus;
                setAgentStatus(status);
                break;
            }

            case 'agent:toolCall': {
                const toolName = payload.toolName as string;
                const args = payload.args as Record<string, unknown>;
                const summary = formatToolCallSummary(toolName, args);
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'tool_call' as const,
                        content: summary,
                        timestamp: new Date(),
                        toolName,
                    },
                ]);
                break;
            }

            case 'agent:toolResult': {
                const toolName = payload.toolName as string;
                const result = payload.result as unknown;
                const summary = formatToolResultSummary(toolName, result);
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'tool_result' as const,
                        content: summary,
                        timestamp: new Date(),
                        toolName,
                    },
                ]);
                break;
            }

            case 'token:usage': {
                const stepDetail = payload.stepDetail as TokenStepDetail | undefined;
                setTokenUsage(prev => ({
                    totalInput: payload.totalInputTokens as number,
                    totalOutput: payload.totalOutputTokens as number,
                    steps: stepDetail ? [...prev.steps, stepDetail] : prev.steps,
                }));
                break;
            }

            case 'run:permissionRequest': {
                const requestId = payload.requestId as string;
                const reason = payload.reason as string;
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'run_request' as const,
                        content: reason,
                        timestamp: new Date(),
                        requestId,
                        runRequestStatus: 'pending' as const,
                    },
                ]);
                break;
            }

            case 'run:status': {
                const status = payload.status as string;
                const runId = payload.runId as string | undefined;
                const error = payload.error as string | undefined;
                let content: string;
                if (status === 'running') {
                    content = 'Graph execution started...';
                } else if (status === 'completed') {
                    content = `Run completed${runId ? ` (${runId.slice(0, 8)})` : ''}`;
                } else {
                    content = `Run failed${error ? `: ${error}` : ''}`;
                }
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'run_status' as const,
                        content,
                        timestamp: new Date(),
                    },
                ]);
                break;
            }

            case 'debug:snapshot': {
                const trigger = payload.trigger as string;
                const files = payload.files as Record<string, string>;
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'debug_snapshot' as const,
                        content: trigger,
                        timestamp: new Date(),
                        debugFiles: files,
                    },
                ]);
                break;
            }

            case 'error': {
                const errorMessage = payload.message as string;
                setEntries(prev => [
                    ...prev,
                    {
                        id: nextEntryId(),
                        type: 'error' as const,
                        content: errorMessage,
                        timestamp: new Date(),
                    },
                ]);
                break;
            }
        }
    }, []);

    // ─── WebSocket ───────────────────────────────────────────────────

    const { connectionState, send } = useAgentWebSocket(graphId, isOpen, handleMessage);
    const sendRef = useRef(send);
    sendRef.current = send;

    // Auto-resume session on reconnect
    const prevConnectionState = useRef<ConnectionState>('disconnected');
    if (connectionState === 'connected' && prevConnectionState.current === 'reconnecting') {
        const storedSessionId = sessionIdRef.current;
        if (storedSessionId) {
            send({ type: 'session:resume', payload: { sessionId: storedSessionId } });
            setSessionStatus('active');
        }
    }
    prevConnectionState.current = connectionState;

    // ─── Actions ─────────────────────────────────────────────────────

    const submit = useCallback(
        (text: string) => {
            if (!text.trim()) return;

            // Add user message to entries
            setEntries(prev => [
                ...prev,
                {
                    id: nextEntryId(),
                    type: 'user_message' as const,
                    content: text.trim(),
                    timestamp: new Date(),
                },
            ]);

            const currentSessionId = sessionIdRef.current;

            if (!currentSessionId) {
                // No active session — start a new one
                const currentGraph = graphRef.current;
                if (!currentGraph) return;

                setSessionStatus('starting');

                sendRef.current({
                    type: 'session:start',
                    payload: {
                        graphId,
                        prompt: text.trim(),
                        model,
                        editMode: 'cli' as const,
                        graph: {
                            nodeInstances: currentGraph.nodeInstances,
                            connections: currentGraph.connections,
                            variables: currentGraph.variables,
                            functions: currentGraph.functions,
                        },
                        metadata: metadata ?? {},
                        secrets: [],
                        spatialContext: spatialContextRef.current,
                    },
                });
            } else {
                // Active session — send follow-up message
                sendRef.current({
                    type: 'user:message',
                    payload: {
                        sessionId: currentSessionId,
                        text: text.trim(),
                    },
                });
            }
        },
        [graphId, model, metadata],
    );

    const cancelOperation = useCallback(() => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId) return;

        sendRef.current({
            type: 'session:pause',
            payload: { sessionId: currentSessionId },
        });
    }, []);

    const setAutoApproveRuns = useCallback((enabled: boolean) => {
        setAutoApproveRunsState(enabled);
        const currentSessionId = sessionIdRef.current;
        if (currentSessionId) {
            sendRef.current({
                type: 'run:toggle',
                payload: { sessionId: currentSessionId, autoApprove: enabled },
            });
        }
    }, []);

    const setDebugMode = useCallback((enabled: boolean) => {
        setDebugModeState(enabled);
        const currentSessionId = sessionIdRef.current;
        if (currentSessionId) {
            sendRef.current({
                type: 'debug:toggle',
                payload: { sessionId: currentSessionId, enabled },
            });
        }
    }, []);

    const respondToRunRequest = useCallback((requestId: string, granted: boolean) => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId) return;

        sendRef.current({
            type: 'run:permissionResponse',
            payload: { sessionId: currentSessionId, requestId, granted },
        });

        // Update the entry status
        setEntries(prev =>
            prev.map(entry =>
                entry.requestId === requestId
                    ? { ...entry, runRequestStatus: granted ? ('approved' as const) : ('denied' as const) }
                    : entry,
            ),
        );
    }, []);

    const endSession = useCallback(() => {
        const currentSessionId = sessionIdRef.current;
        if (currentSessionId) {
            sendRef.current({
                type: 'session:end',
                payload: { sessionId: currentSessionId },
            });
        }

        setSessionId(null);
        sessionIdRef.current = null;
        setSessionStatus('idle');
        setAgentStatus('idle');
        setActiveNodeId(null);
    }, []);

    const toggle = useCallback(() => {
        setIsOpen(prev => {
            if (prev) {
                // Closing — end the session
                endSession();
                setEntries([]);
                setTokenUsage({ totalInput: 0, totalOutput: 0, steps: [] });
                setAutoApproveRunsState(false);
                setDebugModeState(false);
                return false;
            }
            return true;
        });
    }, [endSession]);

    const close = useCallback(() => {
        endSession();
        setEntries([]);
        setTokenUsage({ totalInput: 0, totalOutput: 0, steps: [] });
        setAutoApproveRunsState(false);
        setDebugModeState(false);
        setIsOpen(false);
    }, [endSession]);

    return {
        isOpen,
        toggle,
        close,

        connectionState,

        sessionId,
        sessionStatus,
        agentStatus,

        model,
        setModel,

        entries,

        tokenUsage,

        autoApproveRuns,
        setAutoApproveRuns,
        respondToRunRequest,

        debugMode,
        setDebugMode,

        activeNodeId,

        submit,
        cancelOperation,
    };
}

// ─── Formatting Helpers ────────────────────────────────────────────────

function formatToolCallSummary(toolName: string, args: Record<string, unknown>): string {
    switch (toolName) {
        case 'bash':
            return `$ ${truncate(String(args.command ?? ''), 200)}`;
        case 'readFile':
            return `Reading ${args.path}`;
        case 'writeFile':
            return `Writing ${args.path}`;
        case 'deleteFile':
            return `Deleting ${args.path}`;
        default:
            return `${toolName}(${truncate(JSON.stringify(args), 100)})`;
    }
}

function formatToolResultSummary(_toolName: string, result: unknown): string {
    const text = typeof result === 'string' ? result : JSON.stringify(result);
    return truncate(text, 300);
}

function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
}
